import dayjs from "dayjs";
import { cloneDeep, get, has, isEmpty, isEqual, set } from "lodash-es";
import { defineStore } from "pinia";
import { useRoute } from "vue-router";

import {
  getHotelRatesAsync,
  getHotels,
  getHotelsAsync
} from "@/api/biz/openHotel";
import {
  clearUserRecentHistory,
  getUserRecentHistory,
  setUserRecentHistory
} from "@/api/biz/user";
import { useToggles } from "@/hooks/useToggles";
import { store } from "@/store";
import { useUserStoreHook } from "@/store/modules/user";
import {
  addLocalHistoryItem,
  clearLocalHistory,
  getLocalHistory
} from "@/utils/history";
import { queryToParams } from "@/utils/tool";

export const useHotelStore = defineStore({
  id: "y-hotel",
  state: () => ({
    // 酒店/房型搜索公用参数参数
    searchModel: {
      stay: {
        date: {
          checkIn: dayjs().format("YYYY-MM-DD"),
          checkOut: dayjs().add(1, "day").format("YYYY-MM-DD")
        },
        guest: {
          numberOfRooms: 1,
          numberOfAdults: 2,
          numberOfChildren: 0
        }
      }
    },
    // 酒店搜索参数
    hotelSearchModel: {
      filter: {},
      sort: "byRecommendation",
      location: {},
      commissionPromotion: "", // 是否筛选高佣
      locationRef: "", // locationRef
      locationType: "city", // location类型 city/modelTpHotel/googleMapPolitical
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        total: 0
      }
    },
    /** 选中的酒店 */
    selectedHotel: null,
    /** 选中的房间 */
    selectedRoom: null,
    /** 选中的rate */
    selectedRate: null,

    /**分割线 上面是搜索参数相关，下面是数据展示 */

    /** 酒店列表 */
    hotelList: [],
    /** 历史记录 */
    historyList: [],
    /** 酒店房型列表 */
    roomList: [],
    /** 酒店详情的状态 */
    hotelDetail: {
      isLoading: true,
      isLoaded: false
    },
    hotelInitLoading: true, // 酒店列表初始化的loading
    hotelListInitialLoadDone: false,
    roomListInitLoading: false, // 房型列表初始化的loading
    roomListInitLoadingDone: false, //房型列表初始化的loading是否完成
    hotelListLoadingMore: false, // 酒店列表加载更多loading
    roomListLoadingMore: false, // 房型列表加载更多loading
    hotelListNoMore: false, // 酒店列表没有更多了
    hotelListSearchSnapshot: null,
    hotelListRequestToken: 0,
    extraBrands: [], // 额外的品牌
    extraTags: null, // 额外的促销、权益和酒店标签
    showCommission: true // 是否显示佣金
  }),
  actions: {
    SET_SEARCH_MODEL(model) {
      this.searchModel = { ...this.searchModel, ...model };
    },
    SET_HOTEL_SEARCH_MODEL(model) {
      this.hotelSearchModel = { ...this.hotelSearchModel, ...model };
    },
    SET_SELECTED_HOTEL(hotel) {
      this.selectedHotel = hotel;
    },
    SET_SELECTED_ROOM(room) {
      this.selectedRoom = room;
    },
    SET_SELECTED_RATE(rate) {
      this.selectedRate = rate;
    },
    setRatePriceDetail(priceDetail) {
      if (this.selectedRate) {
        this.selectedRate.priceDetail = priceDetail;
      } else {
        console.warn("没有房型，无法设置价格");
      }
    },
    /**
     * 初始化 Store
     * @param {Object} query - URL 查询参数
     * @param {Boolean} force - 是否强制重置
     */
    initialize(query = {}, force = false) {
      const params = queryToParams(query);
      this.initializeHotelSearchModelFromParams(params);
      if (force) {
        this.resetPagination();
        this.hotelList = [];
        this.hotelListNoMore = false;
        this.hotelListInitialLoadDone = false;
      }
    },
    initializeHotelSearchModelFromParams(params) {
      if (params.stay) {
        if (params.stay.date) {
          this.searchModel.stay.date.checkIn = params.stay.date.checkIn;
          this.searchModel.stay.date.checkOut = params.stay.date.checkOut;
        }
        if (params.stay.guest) {
          this.searchModel.stay.guest.numberOfRooms = Number(
            params.stay.guest.numberOfRooms
          );
          this.searchModel.stay.guest.numberOfAdults = Number(
            params.stay.guest.numberOfAdults
          );
          this.searchModel.stay.guest.numberOfChildren = Number(
            params.stay.guest.numberOfChildren || 0
          );
        }
      }

      if (params.location) {
        this.hotelSearchModel.locationRef = params.location._id;
        this.hotelSearchModel.locationType = params.location.locationType;

        if (params.location.showLabel) {
          this.hotelSearchModel.location = params.location;
        }
      } else {
        this.hotelSearchModel.locationRef = "";
        this.hotelSearchModel.locationType = "";
        this.hotelSearchModel.location = null;
      }

      if (params.filter) {
        this.hotelSearchModel.filter = params.filter;
      }

      if (params.sort) {
        this.hotelSearchModel.sort = params.sort;
      }
    },
    resetPagination() {
      this.hotelSearchModel.pagination = {
        pageIndex: 1,
        pageSize: 10,
        total: 0
      };
    },
    updatePagination(totalCount) {
      this.hotelSearchModel.pagination.pageIndex += 1;
      this.hotelSearchModel.pagination.total = totalCount;
    },
    getHotelListSearchSnapshot() {
      const userStore = useUserStoreHook();

      return {
        preferred: {
          currency: userStore.currency,
          language: userStore.languageISO639Style,
          version: "v1"
        },
        stay: cloneDeep(this.searchModel.stay),
        filter: cloneDeep(this.hotelSearchModel.filter),
        sort: this.hotelSearchModel.sort,
        commissionPromotion: this.hotelSearchModel.commissionPromotion,
        locationRef: this.hotelSearchModel.locationRef,
        locationType: this.hotelSearchModel.locationType
      };
    },
    async initHotelList() {
      if (this.hotelListLoadingMore) return;

      const searchSnapshot = this.getHotelListSearchSnapshot();
      let requestToken = this.hotelListRequestToken;

      if (this.hotelSearchModel.pagination.pageIndex === 1) {
        this.hotelList = [];
        this.hotelListNoMore = false;
        this.hotelInitLoading = true;
        this.hotelListInitialLoadDone = false;
        this.hotelListSearchSnapshot = cloneDeep(searchSnapshot);
        this.hotelListRequestToken += 1;
        requestToken = this.hotelListRequestToken;
      }

      const isCurrentRequest = () => {
        return (
          requestToken === this.hotelListRequestToken &&
          isEqual(this.hotelListSearchSnapshot, searchSnapshot)
        );
      };

      if (
        this.hotelSearchModel.pagination.total > 0 &&
        this.hotelList.length >= this.hotelSearchModel.pagination.total
      ) {
        this.hotelListNoMore = true;
        this.hotelInitLoading = false;
        this.hotelListLoadingMore = false;
        return;
      }

      if (this.hotelList.length > 0) {
        this.hotelListLoadingMore = true;
      }
      const isPriceSort = [
        "byMinBasePrice",
        "byMinBasePriceDescending"
      ].includes(this.hotelSearchModel.sort);
      const userStore = useUserStoreHook();
      const params = {
        preferred: {
          currency: userStore.currency,
          language: userStore.languageISO639Style,
          version: "v1"
        },
        stay: this.searchModel.stay,
        filter: cloneDeep(this.hotelSearchModel.filter),
        paging: {
          limit: this.hotelSearchModel.pagination.pageSize,
          skip:
            (this.hotelSearchModel.pagination.pageIndex - 1) *
            this.hotelSearchModel.pagination.pageSize
        },
        sort: this.hotelSearchModel.sort,
        options: {
          extraBrandsRequired: true,
          extraTagsRequired: true
        }
      };

      if (this.hotelSearchModel.commissionPromotion === "highCommission") {
        set(params, "filter._biz.commissionPromotion", true);
      } else {
        if (has(params, "filter._biz.commissionPromotion")) {
          if (isEmpty(params.filter._biz)) {
            delete params.filter._biz;
          }
        }
      }

      if (this.$router) {
        const query = this.$router.currentRoute.query;
        const paramsFromUrl = queryToParams(query);

        if (!isEmpty(paramsFromUrl.filter)) {
          params.filter = { ...paramsFromUrl.filter, ...params.filter };
        }
        if (paramsFromUrl.options) {
          params.options = { ...params.options, ...paramsFromUrl.options };
        }
      }

      if (!isPriceSort) {
        const brandParams = cloneDeep(params);
        if (has(brandParams, "filter.brands")) {
          delete brandParams.filter.brands;
        }
        getHotelsAsync(brandParams, res => {
          this.extraBrands = get(res, "extra.brands", this.extraBrands);
          this.extraTags = get(res, "extra", this.extraTags);
        });
      }

      getHotelsAsync(
        params,
        res => {
          if (!isCurrentRequest()) return;

          if (res.extra) {
            this.extraBrands = get(res, "extra.brands", this.extraBrands);
            this.extraTags = get(res, "extra", this.extraTags);
          }

          const hotelsData = res.hotels || [];
          if (hotelsData.length === 0 && !res.asyncJobId) {
            this.hotelInitLoading = false;
            this.hotelListLoadingMore = false;
            this.hotelSearchModel.pagination.total = this.hotelList.length;
            this.hotelListNoMore = true;
            return;
          }

          const incomingIds = hotelsData.map(h => h._id);
          const allExist = incomingIds.every(id =>
            this.hotelList.some(h => h._id === id)
          );
          const isUpdateOperation =
            this.hotelList.length > 0 && hotelsData.length > 0 && allExist;

          if (isUpdateOperation) {
            hotelsData.forEach(asyncItem => {
              const index = this.hotelList.findIndex(
                h => h._id === asyncItem._id
              );
              if (index !== -1) {
                this.hotelList[index] = {
                  ...this.hotelList[index],
                  ...asyncItem,
                  _biz: {
                    ...this.hotelList[index]._biz,
                    ...asyncItem._biz,
                    commissionPromotion: Boolean(
                      get(asyncItem, "_biz.commissionPromotion", false)
                    )
                  }
                };
              }
            });
          } else {
            const uniqueHotels = hotelsData.filter(
              newItem => !this.hotelList.some(h => h._id === newItem._id)
            );
            this.hotelList = [...this.hotelList, ...uniqueHotels];

            this.updatePagination(res.paging?.count || this.hotelList.length);
          }

          this.hotelInitLoading = false;
          this.hotelListLoadingMore = false;

          const total = this.hotelSearchModel.pagination.total;
          this.hotelListNoMore = this.hotelList.length >= total;

          if (!this.hotelListInitialLoadDone) {
            this.hotelListInitialLoadDone = true;
          }
        },
        () => {
          if (!isCurrentRequest()) return;

          // 错误处理
          this.hotelInitLoading = false;
          this.hotelListLoadingMore = false;
          if (!this.hotelListInitialLoadDone) {
            this.hotelListInitialLoadDone = true;
          }
        },
        isPriceSort ? { timeout: 180000 } : undefined
      );
    },

    async initHotelDetail() {
      const userStore = useUserStoreHook();
      this.roomList = [];
      this.roomListInitLoading = false;
      this.roomListInitLoadingDone = false;
      this.hotelDetail.isLoading = true;
      this.hotelDetail.isLoaded = false;
      const route = useRoute();
      let _hotelId = route?.query?.hotelId;
      if (!_hotelId && !isEmpty(this.selectedHotel)) {
        _hotelId = this.selectedHotel?._id;
      }
      this.selectedHotel = null;
      const params = {
        preferred: {
          currency: userStore.currency,
          language: userStore.languageISO639Style,
          version: "v1"
        },
        stay: this.searchModel.stay,
        filter: {
          hotels: [{ _id: _hotelId }]
        },
        paging: { limit: 1, skip: 0 }
      };

      try {
        const res = await getHotels(params);
        if (res.success && res.hotels) {
          this.selectedHotel = res.hotels[0];
          this.hotelDetail.isLoaded = true;
          this.hotelDetail.isLoading = false;
        }
      } catch (error) {
        console.error("initHotelDetail 出错", error);
      } finally {
        this.hotelDetail.isLoading = false;
      }
      this.initRoomList();
    },
    async initHotelDetailById(hotelId) {
      if (!hotelId) {
        console.error(" requires a hotelId.");
        return;
      }

      this.hotelDetail.isLoading = true;
      this.hotelDetail.isLoaded = false;
      const userStore = useUserStoreHook();

      const params = {
        preferred: {
          currency: userStore.currency,
          language: userStore.languageISO639Style,
          version: "v1"
        },
        stay: this.searchModel.stay,
        filter: {
          hotels: [{ _id: hotelId }]
        },
        paging: { limit: 1, skip: 0 }
      };

      try {
        const res = await getHotels(params);
        if (res.success && res.hotels && res.hotels.length > 0) {
          this.selectedHotel = res.hotels[0];
          this.hotelDetail.isLoaded = true;
        } else {
          this.selectedHotel = null;
        }
      } catch (error) {
        console.error(`initHotelDetailById:`, error);
      } finally {
        this.hotelDetail.isLoading = false;
      }
    },
    async initRoomList() {
      if (this.roomListInitLoading) return;
      this.roomListInitLoading = true;
      this.roomListInitLoadingDone = false;
      this.roomList = [];
      const userStore = useUserStoreHook();
      const route = useRoute();
      let _hotelId = route?.query?.hotelId;
      if (!_hotelId && !isEmpty(this.selectedHotel)) {
        _hotelId = this.selectedHotel?._id;
      }

      if (!_hotelId) {
        this.roomListInitLoading = false;
        this.roomListInitLoadingDone = true;
        return;
      }

      const params = {
        preferred: {
          currency: userStore.currency,
          language: userStore.languageISO639Style,
          version: "v1"
        },
        stay: this.searchModel.stay,
        hotel: { _id: _hotelId }
      };

      getHotelRatesAsync(
        params,
        res => {
          const newRooms = res.rooms || [];
          let changed = false;

          const newList = newRooms.map(newItem => {
            const oldItem = this.roomList.find(r => r._id === newItem._id);

            if (!oldItem) {
              changed = true;
              return { ...newItem, __isNew: true };
            }

            if (!isEqual(oldItem, newItem)) {
              changed = true;
              return { ...newItem, __isUpdated: true };
            }

            return { ...oldItem, __isSame: true };
          });

          if (changed) {
            this.roomList = newList;
          }

          if (!this.roomListInitLoadingDone) {
            this.roomListInitLoading = false;
            this.roomListInitLoadingDone = true;
          }
        },
        () => {
          this.roomListInitLoading = false;
          this.roomListInitLoadingDone = true;
        }
      );
    },

    initHotelQuery(query) {
      if (query.checkIn) {
        this.searchModel.stay.date.checkIn = query.checkIn;
      }
      if (query.checkOut) {
        this.searchModel.stay.date.checkOut = query.checkOut;
      }
      if (query.numberOfRooms) {
        this.searchModel.stay.guest.numberOfRooms = Number(query.numberOfRooms);
      }
      if (query.numberOfAdults) {
        this.searchModel.stay.guest.numberOfAdults = Number(
          query.numberOfAdults
        );
      }

      this.resetPagination();
    },
    async getSearchHistory() {
      const historyToggle = useToggles("Y_WEB_SEARCH_HISTORY");
      if (!historyToggle.value) {
        this.historyList = [];
        return;
      }

      const { user } = useUserStoreHook();
      if (user) {
        try {
          const response = await getUserRecentHistory({
            type: "searchUniversal"
          });
          this.historyList = response?.results || [];
        } catch (error) {
          this.historyList = [];
        }
      } else {
        this.historyList = getLocalHistory();
      }
    },
    async setSearchHistory(payload) {
      const { user } = useUserStoreHook();
      if (user) {
        try {
          await setUserRecentHistory(payload);
          this.getSearchHistory();
        } catch (error) {
          console.warn("记录【API】搜索历史失败:", error);
        }
      } else {
        addLocalHistoryItem(payload);
        this.historyList = getLocalHistory();
      }
    },
    async deleteHistory() {
      const { user } = useUserStoreHook();
      if (user) {
        try {
          await clearUserRecentHistory({
            type: "searchUniversal"
          });
          this.historyList = [];
        } catch (error) {
          console.warn("记录【API】搜索历史失败:", error);
        }
      } else {
        clearLocalHistory();
        this.historyList = [];
      }
    }
  },
  getters: {
    preferred() {
      const userStore = useUserStoreHook();
      return userStore.preferred;
    },
    duration() {
      const { checkIn, checkOut } = this.searchModel.stay.date || {};
      if (!checkIn || !checkOut) return 0;
      const checkInDate = dayjs(checkIn);
      const checkOutDate = dayjs(checkOut);
      return checkOutDate
        .startOf("day")
        .diff(checkInDate.startOf("day"), "day");
    }
  }
});

export function useHotelStoreHook() {
  return useHotelStore(store);
}
